// frontend/src/pages/Services/LicensePurchaseModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Segmented,
  Select, Space, Table, Tabs, Tag, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import { servicesAPI } from '../../api/client';
import {
  calculatePurchaseQuantity,
  calculatePurchaseTotalCost,
  purchaseQuantityLabel,
} from '../../utils/serviceLicensePurchases';
import { CURRENCY_SYMBOLS, PAYMENT_METHOD_LABELS, formatDate } from './constants';

const { Text } = Typography;

const CURRENCIES = ['USD', 'EUR', 'RUB', 'RSD'];
const PAYMENT_METHODS = [
  { value: 'card', label: 'Карта' },
  { value: 'invoice', label: 'Счёт' },
];
const PURCHASE_TYPE_LABELS = {
  opening_balance: 'Начальный остаток',
  package: 'Пакетом',
  single: 'Поштучно',
};
const PURCHASE_TYPE_COLORS = {
  opening_balance: 'default',
  package: 'purple',
  single: 'blue',
};

function formatMoney(value, currency) {
  if (value === null || value === undefined || value === '') return null;
  const symbol = CURRENCY_SYMBOLS[currency] || currency || '';
  return `${Number(value).toLocaleString('ru-RU')} ${symbol}`.trim();
}

function purchaseCostLabel(purchase) {
  if (purchase.purchase_type === 'package' && purchase.package_price !== null) {
    return `${formatMoney(purchase.package_price, purchase.currency)} × ${purchase.package_count} = ${formatMoney(purchase.total_cost, purchase.currency)}`;
  }
  if (purchase.purchase_type === 'single' && purchase.unit_price !== null) {
    return `${formatMoney(purchase.unit_price, purchase.currency)} × ${purchase.quantity} = ${formatMoney(purchase.total_cost, purchase.currency)}`;
  }
  return '—';
}

export default function LicensePurchaseModal({
  open,
  onClose,
  group = null,
  service = null,
  selectService = false,
  initialTab = 'add',
  initialEditPurchase = null,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [createdGroupId, setCreatedGroupId] = useState(null);

  const purchaseType = Form.useWatch('purchase_type', form) || 'single';
  const quantity = Form.useWatch('quantity', form);
  const packageCount = Form.useWatch('package_count', form);
  const licensesPerPackage = Form.useWatch('licenses_per_package', form);
  const unitPrice = Form.useWatch('unit_price', form);
  const packagePrice = Form.useWatch('package_price', form);
  const selectedServiceId = Form.useWatch('service_id', form);
  const currency = Form.useWatch('currency', form) || group?.currency || 'USD';
  const editPurchaseType = Form.useWatch('purchase_type', editForm);

  const targetServiceId = service?.id || selectedServiceId || group?.service_id || null;

  const { data: services = [] } = useQuery({
    queryKey: ['services', 'purchase-selector'],
    queryFn: () => servicesAPI.getAll().then(r => r.data),
    enabled: !!open && selectService,
  });

  const { data: serviceDetail } = useQuery({
    queryKey: ['service-detail', targetServiceId],
    queryFn: () => servicesAPI.getOne(targetServiceId).then(r => r.data),
    enabled: !!open && !!targetServiceId && !group?.id,
  });

  const purchasedGroups = (serviceDetail?.license_groups || [])
    .filter(item => item.is_active && item.license_type === 'purchased');
  const existingGroup = purchasedGroups.length === 1 ? purchasedGroups[0] : null;
  const historyGroupId = group?.id || existingGroup?.id || createdGroupId || null;
  const hasAmbiguousPurchasedGroups = purchasedGroups.length > 1;

  useEffect(() => {
    if (!open || !selectService) return;
    setCreatedGroupId(null);
    if (activeTab === 'history') setActiveTab('add');
  }, [open, selectService, selectedServiceId]);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab);
    setEditingPurchase(null);
    setCreatedGroupId(null);
    form.resetFields();
    editForm.resetFields();
    form.setFieldsValue({
      service_id: service?.id || undefined,
      purchase_type: 'single',
      currency: group?.currency || 'USD',
      payment_method: group?.payment_method || undefined,
      purchased_at: dayjs(),
    });
  }, [
    open, service?.id, group?.id, group?.currency, group?.payment_method,
    initialTab, form, editForm,
  ]);

  const { data: purchases = [], isLoading: historyLoading } = useQuery({
    queryKey: ['license-purchases', historyGroupId],
    queryFn: () => servicesAPI.getLicensePurchases(historyGroupId).then(r => r.data),
    enabled: !!open && !!historyGroupId,
  });

  const invalidatePurchaseData = (groupId = historyGroupId) => {
    if (groupId) queryClient.invalidateQueries(['license-purchases', groupId]);
    if (targetServiceId) queryClient.invalidateQueries(['service-detail', targetServiceId]);
    queryClient.invalidateQueries(['services']);
    queryClient.invalidateQueries(['services-stats']);
    onSuccess?.();
  };

  const createMutation = useMutation({
    mutationFn: (data) => {
      if (group?.id) return servicesAPI.createLicensePurchase(group.id, data);
      if (!targetServiceId) throw new Error('Выберите сервис');
      return servicesAPI.createServiceLicensePurchase(targetServiceId, data);
    },
    onSuccess: (response) => {
      const groupId = response?.data?.license_group_id || group?.id || historyGroupId;
      if (groupId) setCreatedGroupId(groupId);
      message.success('Закупка добавлена');
      invalidatePurchaseData(groupId);
      setActiveTab('history');
    },
    onError: (e) => message.error(e?.response?.data?.detail || e?.message || 'Ошибка добавления закупки'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => servicesAPI.updateLicensePurchase(historyGroupId, editingPurchase.id, data),
    onSuccess: () => {
      message.success('Закупка обновлена');
      invalidatePurchaseData();
      setEditingPurchase(null);
      editForm.resetFields();

      if (initialEditPurchase) {
        onClose();
      }
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка обновления приобретения'),
  });

  const preview = useMemo(() => ({
    purchase_type: purchaseType,
    quantity,
    package_count: packageCount,
    licenses_per_package: licensesPerPackage,
    unit_price: unitPrice,
    package_price: packagePrice,
  }), [purchaseType, quantity, packageCount, licensesPerPackage, unitPrice, packagePrice]);

  const previewQuantity = calculatePurchaseQuantity(preview);
  const previewCost = calculatePurchaseTotalCost(preview);

  const handleAdd = () => {
    form.validateFields().then(values => {
      const common = {
        purchase_type: values.purchase_type,
        currency: values.currency,
        purchased_at: values.purchased_at ? values.purchased_at.format('YYYY-MM-DD') : null,
        payment_method: values.payment_method || null,
        notes: values.notes?.trim() || null,
        contract_url: values.contract_url?.trim() || null,
        purchase_url: values.purchase_url?.trim() || null,
      };

      if (values.purchase_type === 'package') {
        createMutation.mutate({
          ...common,
          package_count: values.package_count,
          licenses_per_package: values.licenses_per_package,
          package_price: values.package_price ?? null,
        });
        return;
      }

      createMutation.mutate({
        ...common,
        quantity: values.quantity,
        unit_price: values.unit_price ?? null,
      });
    });
  };

  const openEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    editForm.resetFields();
    editForm.setFieldsValue({
      purchase_type: purchase.purchase_type,
      quantity: purchase.quantity,
      package_count: purchase.package_count,
      licenses_per_package: purchase.licenses_per_package,
      unit_price: purchase.unit_price == null ? null : Number(purchase.unit_price),
      package_price: purchase.package_price == null ? null : Number(purchase.package_price),
      currency: purchase.currency || group?.currency || existingGroup?.currency || 'USD',
      purchased_at: purchase.purchased_at ? dayjs(purchase.purchased_at) : null,
      payment_method: purchase.payment_method || undefined,
      notes: purchase.notes || '',
      contract_url: purchase.contract_url || '',
      purchase_url: purchase.purchase_url || '',
    });
  };

  const closeEditPurchase = () => {
    setEditingPurchase(null);
    editForm.resetFields();

    if (initialEditPurchase) {
      onClose();
    }
  };

  useEffect(() => {
    if (!open || !initialEditPurchase) return;
    setActiveTab('history');
    openEditPurchase(initialEditPurchase);
  }, [open, initialEditPurchase?.id]);

  const handleEdit = () => {
    editForm.validateFields().then(values => {
      const common = {
        purchase_type: values.purchase_type,
        purchased_at: values.purchased_at ? values.purchased_at.format('YYYY-MM-DD') : null,
        payment_method: values.payment_method || null,
        notes: values.notes?.trim() || null,
        contract_url: values.contract_url?.trim() || null,
        purchase_url: values.purchase_url?.trim() || null,
      };

      if (editingPurchase.purchase_type === 'opening_balance') {
        if (values.purchase_type === 'opening_balance') {
          updateMutation.mutate(common);
          return;
        }
        if (values.purchase_type === 'package') {
          updateMutation.mutate({
            ...common,
            package_count: values.package_count,
            licenses_per_package: values.licenses_per_package,
            package_price: values.package_price ?? null,
            currency: values.currency,
          });
          return;
        }
        updateMutation.mutate({
          ...common,
          quantity: editingPurchase.quantity,
          unit_price: values.unit_price ?? null,
          currency: values.currency,
        });
        return;
      }

      if (editingPurchase.purchase_type === 'package') {
        updateMutation.mutate({
          ...common,
          package_count: values.package_count,
          licenses_per_package: values.licenses_per_package,
          package_price: values.package_price ?? null,
          currency: values.currency,
        });
        return;
      }

      updateMutation.mutate({
        ...common,
        quantity: values.quantity,
        unit_price: values.unit_price ?? null,
        currency: values.currency,
      });
    });
  };

  const historyColumns = [
    {
      title: 'Дата', key: 'date', width: 105,
      render: (_, row) => <Text style={{ fontSize: 12 }}>{formatDate(row.purchased_at || row.created_at)}</Text>,
    },
    {
      title: 'Как куплено', dataIndex: 'purchase_type', key: 'type', width: 135,
      render: (value) => <Tag color={PURCHASE_TYPE_COLORS[value]}>{PURCHASE_TYPE_LABELS[value] || value}</Tag>,
    },
    {
      title: 'Лицензии', key: 'quantity', width: 125,
      render: (_, row) => <Text>{purchaseQuantityLabel(row)}</Text>,
    },
    {
      title: 'Стоимость', key: 'cost', width: 160,
      render: (_, row) => <Text>{purchaseCostLabel(row)}</Text>,
    },
    {
      title: 'Оплата', dataIndex: 'payment_method', key: 'payment', width: 90,
      render: (value) => value ? PAYMENT_METHOD_LABELS[value] || value : '—',
    },
    {
      title: 'Договор', dataIndex: 'contract_url', key: 'contract_url', width: 90,
      render: (value) => value
        ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            Открыть
          </a>
        )
        : '—',
    },
    {
      title: 'Закупка', dataIndex: 'purchase_url', key: 'purchase_url', width: 90,
      render: (value) => value
        ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            Открыть
          </a>
        )
        : '—',
    },
  ];

  const addForm = (
    <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
      {selectService ? (
        <Form.Item
          name="service_id"
          label="Сервис"
          rules={[{ required: true, message: 'Выберите сервис' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Выберите сервис"
            options={services.map(item => ({ value: item.id, label: item.name }))}
          />
        </Form.Item>
      ) : service?.id ? (
        <Form.Item label="Сервис">
          <Input value={service.name || ''} readOnly />
        </Form.Item>
      ) : null}

      {hasAmbiguousPurchasedGroups && (
        <Text type="danger" style={{ display: 'block', marginBottom: 16 }}>
          У сервиса найдено несколько активных групп купленных лицензий. Сначала проверьте данные сервиса.
        </Text>
      )}

      <Form.Item name="purchase_type" label="Способ покупки" rules={[{ required: true }]}>
        <Segmented
          block
          options={[
            { label: 'Поштучно', value: 'single' },
            { label: 'Пакетом', value: 'package' },
          ]}
        />
      </Form.Item>

      {purchaseType === 'package' ? (
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="package_count" label="Количество пакетов" rules={[{ required: true, message: 'Укажите количество пакетов' }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="licenses_per_package" label="Лицензий в пакете" rules={[{ required: true, message: 'Укажите размер пакета' }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="package_price" label="Цена пакета">
              <InputNumber min={0} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      ) : (
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="quantity" label="Количество лицензий" rules={[{ required: true, message: 'Укажите количество лицензий' }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="unit_price" label="Цена за лицензию">
              <InputNumber min={0} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="currency" label="Валюта" rules={[{ required: true }]}>
            <Select options={CURRENCIES.map(value => ({ value, label: value }))} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="purchased_at"
            label="Дата покупки"
            rules={[{ required: true, message: 'Укажите дату покупки' }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="payment_method" label="Способ оплаты">
            <Select allowClear options={PAYMENT_METHODS} placeholder="Не указан" />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <Space size={24} wrap>
          <Text>Будет добавлено: <Text strong>{previewQuantity || '—'}</Text></Text>
          <Text>
            Общая стоимость: <Text strong>{previewCost === null ? '—' : formatMoney(previewCost, currency)}</Text>
          </Text>
        </Space>
      </div>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="contract_url" label="Ссылка на договор">
            <Input placeholder="https://..." />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="purchase_url" label="Ссылка на закупку">
            <Input placeholder="https://..." />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="notes" label="Примечание" style={{ marginBottom: 0 }}>
        <Input.TextArea rows={2} />
      </Form.Item>
    </Form>
  );

  const editingOpeningBalance = editingPurchase?.purchase_type === 'opening_balance';

  const editFormContent = editingPurchase ? (
    <Form form={editForm} layout="vertical" style={{ marginTop: 8 }}>
      {editingOpeningBalance ? (
        <>
          <Form.Item name="purchase_type" label="Способ покупки" rules={[{ required: true }]}>
            <Segmented
              block
              options={[
                { label: 'Начальный остаток', value: 'opening_balance' },
                { label: 'Поштучно', value: 'single' },
                { label: 'Пакетом', value: 'package' },
              ]}
            />
          </Form.Item>
          <Text type="secondary">
            Можно уточнить исторические данные. Общее количество лицензий ({editingPurchase.quantity}) не изменится.
          </Text>
        </>
      ) : (
        <Form.Item label="Как куплено">
          <Tag color={PURCHASE_TYPE_COLORS[editingPurchase.purchase_type]}>
            {PURCHASE_TYPE_LABELS[editingPurchase.purchase_type] || editingPurchase.purchase_type}
          </Tag>
        </Form.Item>
      )}

      {(editingPurchase.purchase_type === 'single'
        || (editingOpeningBalance && editPurchaseType === 'single')) && (
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="quantity" label="Количество лицензий" rules={[{ required: true }]}>
              <InputNumber
                min={1}
                precision={0}
                disabled={editingOpeningBalance}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="unit_price" label="Цена за лицензию">
              <InputNumber min={0} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {(editingPurchase.purchase_type === 'package'
        || (editingOpeningBalance && editPurchaseType === 'package')) && (
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="package_count" label="Количество пакетов" rules={[{ required: true, message: 'Укажите количество пакетов' }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="licenses_per_package" label="Лицензий в пакете" rules={[{ required: true, message: 'Укажите размер пакета' }]}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="package_price" label="Цена пакета">
              <InputNumber min={0} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {(!editingOpeningBalance || editPurchaseType !== 'opening_balance') && (
        <Form.Item name="currency" label="Валюта" rules={[{ required: true }]}>
          <Select options={CURRENCIES.map(value => ({ value, label: value }))} />
        </Form.Item>
      )}

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="purchased_at"
            label="Дата покупки"
            rules={editPurchaseType !== 'opening_balance'
              ? [{ required: true, message: 'Укажите дату покупки' }]
              : []}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="payment_method" label="Способ оплаты">
            <Select allowClear options={PAYMENT_METHODS} placeholder="Не указан" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="contract_url" label="Ссылка на договор">
            <Input placeholder="https://..." />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="purchase_url" label="Ссылка на закупку">
            <Input placeholder="https://..." />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="notes" label="Примечание">
        <Input.TextArea rows={2} />
      </Form.Item>
    </Form>
  ) : null;

  const history = (
    <>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
      >
        Нажмите на запись, чтобы открыть или изменить данные
      </Text>
      <div
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <Table
          rowKey="id"
          size="small"
          loading={historyLoading}
          dataSource={purchases}
          columns={historyColumns}
          pagination={false}
          locale={{ emptyText: 'История приобретений пуста' }}
          scroll={{ x: 850 }}
          onRow={(row) => ({
            onClick: () => openEditPurchase(row),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </>
  );

  return (
    <>
      <Modal
        open={open && !editingPurchase}
        title={selectService ? 'Добавить лицензии' : `Закупки лицензий${service?.name ? ` — ${service.name}` : ''}`}
        onCancel={onClose}
        onOk={handleAdd}
        okText="Добавить закупку"
        cancelText={activeTab === 'add' ? 'Отмена' : 'Закрыть'}
        confirmLoading={createMutation.isPending}
        okButtonProps={{
          disabled: activeTab === 'add' && hasAmbiguousPurchasedGroups,
          style: { display: activeTab === 'add' ? undefined : 'none' },
        }}
        width={900}
        destroyOnClose
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'add', label: 'Добавить закупку', children: addForm },
            { key: 'history', label: `История приобретений (${purchases.length})`, children: history, disabled: !historyGroupId },
          ]}
        />
      </Modal>

      <Modal
        open={!!editingPurchase}
        title="Редактировать закупку"
        onCancel={closeEditPurchase}
        onOk={handleEdit}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={updateMutation.isPending}
        width={680}
        destroyOnClose
      >
        {editFormContent}
      </Modal>
    </>
  );
}
